import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Shield, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { isValidEmail, truncate } from '../lib/validation';

const OTP_COOLDOWN_SECONDS = 60;
const OTP_COOLDOWN_STORAGE_KEY = 'otpCooldownEnd';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [otpSent, setOtpSent] = useState(false);
  const [emailError, setEmailError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const getRemainingSeconds = () => {
      const cooldownUntil = Number(sessionStorage.getItem(OTP_COOLDOWN_STORAGE_KEY));

      if (!cooldownUntil) return 0;

      const remainingSeconds = Math.ceil((cooldownUntil - Date.now()) / 1000);

      if (remainingSeconds <= 0) {
        sessionStorage.removeItem(OTP_COOLDOWN_STORAGE_KEY);
        return 0;
      }

      return remainingSeconds;
    };

    const initialRemainingSeconds = getRemainingSeconds();
    setCooldown(initialRemainingSeconds);
    setOtpSent(initialRemainingSeconds > 0);

    const timer = setInterval(() => {
      setCooldown(getRemainingSeconds());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmedEmail = email.trim();
    const trimmedName = truncate(name.trim(), 50);

    setEmail(trimmedEmail);
    setName(trimmedName);

    if (!trimmedEmail || cooldown > 0) return;

    if (!isValidEmail(trimmedEmail)) {
      setEmailError('Please enter a valid email address');
      return;
    }

    setEmailError('');
    setLoading(true);

    try {
      // Use direct fetch to avoid supabase-js "body stream" bug with error responses
      const res = await fetch(
        `${process.env.REACT_APP_SUPABASE_URL}/auth/v1/otp`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
          },
          body: JSON.stringify({
            email: trimmedEmail,
            data: trimmedName ? { name: trimmedName } : {},
            create_user: true,
            gotrue_meta_security: {},
          }),
        }
      );

      if (res.status === 429) {
        toast.error('Too many requests. Please wait a minute and try again.');
        return;
      }

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        toast.error(errData.msg || errData.error_description || 'Failed to send OTP');
        return;
      }

      toast.success('OTP sent to your email!');
      sessionStorage.setItem(
        OTP_COOLDOWN_STORAGE_KEY,
        String(Date.now() + OTP_COOLDOWN_SECONDS * 1000)
      );
      setOtpSent(true);
      setCooldown(OTP_COOLDOWN_SECONDS);
      navigate('/verify-otp', { state: { email: trimmedEmail } });
    } catch (error) {
      toast.error('Network error. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Image */}
      <div
        className="hidden lg:flex lg:w-1/2 relative overflow-hidden"
        style={{
          backgroundImage: 'url(https://static.prod-images.emergentagent.com/jobs/7c1de106-ee36-4e18-a9e4-f71f970980ea/images/47bec2839d9882d2ab111193c2b4f3dd83c2fb92f19111c9b303c1f0acf3ec2b.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-black/40" />
        <div className="relative z-10 flex flex-col justify-center px-12 text-white">
          <h2 className="text-3xl font-semibold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Access Your Vault
          </h2>
          <p className="text-lg text-white/80">
            Passwordless login via email OTP. No passwords to remember.
          </p>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center px-4 sm:px-6 lg:px-8 bg-background">
        <div className="w-full max-w-md">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-8 transition-colors duration-150">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>

          <div className="flex items-center gap-3 mb-8">
            <Shield className="w-10 h-10 text-primary" />
            <h1 className="text-2xl sm:text-3xl font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="login-title">
              Sign In
            </h1>
          </div>

          <p className="text-sm text-muted-foreground mb-6">
            Enter your email to receive a one-time verification code. New users are automatically registered.
          </p>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label htmlFor="name">Display Name (optional for new users)</Label>
              <Input
                id="name"
                type="text"
                placeholder="John Doe"
                value={name}
                onChange={(e) => setName(e.target.value.slice(0, 50))}
                maxLength={50}
                data-testid="login-name-input"
                className="mt-2"
              />
            </div>

            <div>
              <Label htmlFor="email">Email Address</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value.slice(0, 100));
                  if (emailError) setEmailError('');
                }}
                maxLength={100}
                required
                aria-invalid={!!emailError}
                aria-describedby={emailError ? 'login-email-error' : undefined}
                data-testid="login-email-input"
                className={`mt-2 ${emailError ? 'border-destructive focus-visible:ring-destructive' : ''}`}
              />
              {emailError && (
                <p id="login-email-error" className="mt-2 text-xs text-destructive" data-testid="login-email-error">
                  {emailError}
                </p>
              )}
            </div>

            <Button type="submit" className="w-full" disabled={loading || cooldown > 0} data-testid="login-submit-btn">
              {loading ? 'Sending OTP...' : otpSent ? 'Resend OTP' : 'Send OTP Code'}
            </Button>
            {cooldown > 0 && (
              <p className="text-xs text-center text-muted-foreground font-mono" data-testid="otp-cooldown">
                Resend available in {cooldown}s...
              </p>
            )}
          </form>

          <div className="mt-8 p-4 bg-muted rounded-lg">
            <p className="text-xs text-muted-foreground leading-relaxed">
              We'll send a verification code to your email. Check your inbox (and spam folder).
              The code expires in 5 minutes.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
